import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { League, Castaway, Draft } from '../types';

const AdminPlayerSwaps: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [castaways, setCastaways] = useState<Castaway[]>([]);
  const [drafts, setDrafts] = useState<{ [key: string]: Draft }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [manualSwapUser, setManualSwapUser] = useState<string>('');
  const [manualSwapOut, setManualSwapOut] = useState<number | null>(null);
  const [manualSwapIn, setManualSwapIn] = useState<number | null>(null);
  const [manualSwapEpisode, setManualSwapEpisode] = useState<number>(1);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('adminSelectedSeason');
    return saved ? parseInt(saved) : 49;
  });

  const fetchLeagues = async (season: number) => {
    console.log('AdminPlayerSwaps: Fetching leagues for season', season);
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      const data = await response.json();
      
      const filteredLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      console.log('AdminPlayerSwaps: Found leagues:', filteredLeagues.length);
      setLeagues(filteredLeagues);
      
      if (filteredLeagues.length > 0) {
        setSelectedLeagueId(filteredLeagues[0].id);
      } else {
        setSelectedLeagueId('');
      }
    } catch (err) {
      console.error('AdminPlayerSwaps: Error fetching leagues:', err);
      setError('Failed to fetch leagues.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues(selectedSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '49');
      console.log('AdminPlayerSwaps: Season changed event to', newSeason);
      setSelectedSeason(newSeason);
      fetchLeagues(newSeason);
      setCastaways([]);
      setDrafts({});
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, []);

  useEffect(() => {
    if (selectedLeagueId) {
      const fetchLeagueData = async () => {
        setIsLoading(true);
        try {
          const [draftsResponse, castawaysResponse] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/drafts`),
            fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways`),
          ]);
          const draftsData = await draftsResponse.json();
          const castawaysData = await castawaysResponse.json();
          setDrafts(draftsData.drafts || {});
          setCastaways(castawaysData.castaways || []);
        } catch (err) {
          setError('Failed to fetch league data.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchLeagueData();
    }
  }, [selectedLeagueId]);

  const handleSwapUpdate = async (userName: string, swapUsed: boolean) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/leagues/${selectedLeagueId}/swap`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName, swapUsed }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setSuccess('Swap status updated successfully!');
      // Refresh drafts
      const draftsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/drafts`);
      const draftsData = await draftsResponse.json();
      setDrafts(draftsData.drafts || {});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSwap = async (userName: string) => {
    if (!window.confirm(`Are you sure you want to reset the swap for ${userName}? This cannot be undone.`)) {
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/leagues/${selectedLeagueId}/reset-swap`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setSuccess(`Swap for ${userName} reset successfully!`);
      // Refresh drafts
      const draftsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/drafts`);
      const draftsData = await draftsResponse.json();
      setDrafts(draftsData.drafts || {});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSwap = async () => {
    if (!manualSwapUser || !manualSwapOut || !manualSwapIn || !manualSwapEpisode) {
      setError('Please fill out all fields for the manual swap.');
      return;
    }
    if (!window.confirm(`Are you sure you want to perform this manual swap for ${manualSwapUser}?`)) {
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/leagues/${selectedLeagueId}/manual-swap`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: manualSwapUser,
          swappedOutCastawayId: manualSwapOut,
          swappedInCastawayId: manualSwapIn,
          swapEffectiveEpisode: manualSwapEpisode,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setSuccess(`Manual swap for ${manualSwapUser} successful!`);
      // Refresh drafts
      const draftsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/drafts`);
      const draftsData = await draftsResponse.json();
      setDrafts(draftsData.drafts || {});
      // Reset form
      setManualSwapUser('');
      setManualSwapOut(null);
      setManualSwapIn(null);
      setManualSwapEpisode(1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getCastawayName = (id: number) => castaways.find(c => c.id === id)?.name || 'Unknown';
  const userDraft = drafts[manualSwapUser];
  const originalTeamIds = userDraft?.originalTeam || userDraft?.team || [];

  return (
    <Container className="mt-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card shadow-sm home-card>
        <Card.Header as="h4" className="card-title-custom text-center">Admin - Player Swaps (Season {selectedSeason})</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          {leagues.length > 1 && (
            <Form.Group className="mb-3">
              <Form.Label>Select League</Form.Label>
              <Form.Select value={selectedLeagueId} onChange={(e) => setSelectedLeagueId(e.target.value)} disabled={isLoading}>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Form.Select>
            </Form.Group>
          )}

          {leagues.length === 1 && (
            <div className="mb-3 p-2 bg-light border rounded">
              <strong>League:</strong> {leagues[0].name}
            </div>
          )}

          {leagues.length === 0 && !isLoading && (
             <Alert variant="info">No leagues found for Season {selectedSeason}.</Alert>
          )}

          {isLoading && <div className="text-center my-3"><Spinner animation="border" /></div>}
          {selectedLeagueId && !isLoading && (
            <>
              <ListGroup>
                {Object.entries(drafts).map(([userName, draft]) => (
                  <ListGroup.Item key={userName}>
                    <strong>{userName}</strong>
                    <Row className="mt-2 align-items-center">
                      <Col md={4}>
                        Current Team: {draft.team.map(getCastawayName).join(', ')}
                      </Col>
                      <Col md={3}>
                        Original Team: {draft.originalTeam ? draft.originalTeam.map(getCastawayName).join(', ') : 'N/A'}
                      </Col>
                      <Col md={2}>
                        <Form.Check
                          type="switch"
                          id={`swap-used-${userName}`}
                          label="Swap Used"
                          checked={draft.swapUsed || false}
                          onChange={(e) => handleSwapUpdate(userName, e.target.checked)}
                        />
                      </Col>
                      <Col md={3}>
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleResetSwap(userName)}
                          disabled={!draft.swapUsed && !draft.originalTeam}
                        >
                          Reset Swap
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <hr />

              <h5>Manual Swap</h5>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>User</Form.Label>
                    <Form.Select value={manualSwapUser} onChange={e => setManualSwapUser(e.target.value)}>
                      <option value="">-- Select User --</option>
                      {Object.keys(drafts).map(userName => <option key={userName} value={userName}>{userName}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Swap Out</Form.Label>
                    <Form.Select value={manualSwapOut || ''} onChange={e => setManualSwapOut(Number(e.target.value))} disabled={!manualSwapUser}>
                      <option value="">-- Select Player --</option>
                      {originalTeamIds.map(id => <option key={id} value={id}>{getCastawayName(id)}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Swap In</Form.Label>
                    <Form.Select value={manualSwapIn || ''} onChange={e => setManualSwapIn(Number(e.target.value))}>
                      <option value="">-- Select Player --</option>
                      {castaways.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Effective Episode</Form.Label>
                    <Form.Control type="number" value={manualSwapEpisode} onChange={e => setManualSwapEpisode(Number(e.target.value))} />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="primary" className="mt-3" onClick={handleManualSwap}>Submit Manual Swap</Button>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminPlayerSwaps;
